import type { ImportedModelTabType } from './tab-type'
import type { ProjectModelLoadResult } from '../../project-model/loading/project-model-load-session'
import type { ImportedModelResult } from './types'

/**
 * 导入文档能力快照。
 */
export interface ImportedDocumentCapabilities {
  /** 当前文档是否允许展示特征工具栏。 */
  canShowFeatureToolbarTab: boolean
  /** 当前文档是否允许执行特征命令。 */
  canExecuteFeatureCommands: boolean
}

/**
 * 导入文档中的特征工具栏命令。
 */
export type ImportedDocumentFeatureToolbarCommand =
  | 'extrude'
  | 'extrude-cut'
  | 'revolve-boss'
  | 'revolve-cut'
  | 'fillet'
  | 'chamfer'

/**
 * 导入文档中的特征树可编辑特征种类。
 */
export type ImportedDocumentFeatureKind =
  | 'boss-extrude'
  | 'cut-extrude'
  | 'boss-revolve'
  | 'cut-revolve'
  | 'fillet'
  | 'chamfer'

/** 零件文档能力。 */
const PART_DOCUMENT_CAPABILITIES: ImportedDocumentCapabilities = Object.freeze({
  canShowFeatureToolbarTab: true,
  canExecuteFeatureCommands: true,
})

/** 装配体文档能力。 */
const ASSEMBLY_DOCUMENT_CAPABILITIES: ImportedDocumentCapabilities = Object.freeze({
  canShowFeatureToolbarTab: false,
  canExecuteFeatureCommands: false,
})

/** 特征工具栏命令集合。 */
const FEATURE_TOOLBAR_COMMAND_SET: ReadonlySet<ImportedDocumentFeatureToolbarCommand> = new Set([
  'extrude',
  'extrude-cut',
  'revolve-boss',
  'revolve-cut',
  'fillet',
  'chamfer',
])

/** 特征树可编辑特征种类集合。 */
const FEATURE_KIND_SET: ReadonlySet<ImportedDocumentFeatureKind> = new Set([
  'boss-extrude',
  'cut-extrude',
  'boss-revolve',
  'cut-revolve',
  'fillet',
  'chamfer',
])

/**
 * 解析导入文档的持久化能力状态。
 *
 * 旧加载结果没有该字段，必须按 `stable` 处理以保持现有保存行为；只有新加载器明确
 * 标记 `runtime-only` 时，保存边界才会拒绝持久化。
 *
 * @param result 导入模型或项目模型加载结果。
 * @returns 当前文档的持久化能力。
 */
export function resolveImportedDocumentPersistenceStatus(
  result: ImportedModelResult | ProjectModelLoadResult,
): PuppyCAD.ImportedModelPersistenceStatus {
  return result.persistenceStatus ?? 'stable'
}

/**
 * 断言当前导入文档可以跨会话持久化。
 *
 * 所有导出与 RPC 入口必须在产生字节前复用该断言。complete-glb 当前输出 `stable`，
 * 因此会进入正常保存链路；只有其它加载器明确返回 `runtime-only` 时才会被拒绝。
 *
 * @param result 导入模型或项目模型加载结果。
 */
export function assertImportedDocumentCanPersist(
  result: ImportedModelResult | ProjectModelLoadResult,
): void {
  if (resolveImportedDocumentPersistenceStatus(result) === 'runtime-only') {
    throw new Error('当前文档仅支持运行时加载，尚不支持保存')
  }
}

/**
 * 解析导入文档的交互能力。
 *
 * 设计说明：
 * 1. `part/assembly` 的能力差异属于文档语义，不能散落在 UI 组件里判断；
 * 2. 工具栏、命令分发、属性面板和快捷键都应依赖同一份能力模型；
 * 3. 未知类型在加载早期按零件能力兜底，避免 tab 类型尚未解析时出现不必要的 UI 抖动。
 *
 * @param tabType 当前激活标签页的类型。
 * @returns 当前文档允许的能力集合。
 */
export function resolveImportedDocumentCapabilities(
  tabType: ImportedModelTabType | null | undefined,
): ImportedDocumentCapabilities {
  if (tabType === 'assembly') {
    return ASSEMBLY_DOCUMENT_CAPABILITIES
  }
  return PART_DOCUMENT_CAPABILITIES
}

/**
 * 判断工具栏命令是否属于导入文档的特征命令。
 *
 * @param commandType 待判断命令。
 * @returns 是否为特征工具栏命令。
 */
export function isImportedDocumentFeatureToolbarCommand(
  commandType: string,
): commandType is ImportedDocumentFeatureToolbarCommand {
  return FEATURE_TOOLBAR_COMMAND_SET.has(commandType as ImportedDocumentFeatureToolbarCommand)
}

/**
 * 判断特征树节点种类是否属于导入文档的特征编辑类型。
 *
 * @param featureKind 待判断特征种类。
 * @returns 是否为可被文档能力约束的特征种类。
 */
export function isImportedDocumentFeatureKind(
  featureKind: string,
): featureKind is ImportedDocumentFeatureKind {
  return FEATURE_KIND_SET.has(featureKind as ImportedDocumentFeatureKind)
}

/**
 * 判断当前文档能力是否允许执行指定工具栏命令。
 *
 * @param capabilities 当前导入文档能力。
 * @param commandType 待执行命令。
 * @returns 非特征命令或允许特征命令时返回 `true`。
 */
export function canExecuteImportedDocumentToolbarCommand(
  capabilities: ImportedDocumentCapabilities,
  commandType: string,
): boolean {
  return !isImportedDocumentFeatureToolbarCommand(commandType)
    || capabilities.canExecuteFeatureCommands === true
}
